import { useMemo, useState } from 'react';
import { DRUM_ROWS } from '../instruments/drum/DrumSynth';
import { noteName } from '../core/project/types';
import { useAppStore } from '../state/store';
import { PianoRoll } from './PianoRoll';
import { Playlist } from './Playlist';
import { Row, StepGrid } from './PatternEditor';
import { SampleEditor } from './SampleEditor';
import { SessionView } from './SessionView';

const SYNTH_TOP = 72; // C5
const SYNTH_BOTTOM = 36; // C2

/**
 * Center panel: tabs for the pattern editor (step grid for drums, piano
 * roll for synths) and the playlist/arrangement timeline.
 */
export function CenterPanel() {
  const [view, setView] = useState<'pattern' | 'playlist' | 'session'>('pattern');
  const project = useAppStore((s) => s.project);
  const selectedId = useAppStore((s) => s.selectedChannelId);
  const channel = project.channels.find((c) => c.id === selectedId) ?? null;
  const pattern = channel ? project.patterns[channel.patternId] : null;

  const rows = useMemo<Row[]>(() => {
    if (!channel) return [];
    if (channel.kind === 'drum') {
      return DRUM_ROWS.map((r) => ({ note: r.note, label: r.label }));
    }
    const out: Row[] = [];
    for (let n = SYNTH_TOP; n >= SYNTH_BOTTOM; n--) {
      out.push({ note: n, label: noteName(n) });
    }
    return out;
  }, [channel]);

  const { toggleStep, setPatternLength, newPattern } = useAppStore.getState();

  return (
    <main className="panel panel-center">
      <div className="panel-title">
        <span className="view-tabs">
          <button className={view === 'pattern' ? 'active' : ''} onClick={() => setView('pattern')}>
            Pattern
          </button>
          <button className={view === 'playlist' ? 'active' : ''} onClick={() => setView('playlist')}>
            Playlist
          </button>
          <button className={view === 'session' ? 'active' : ''} onClick={() => setView('session')}>
            Session
          </button>
        </span>
        {view === 'pattern' && pattern && (
          <span className="pe-controls">
            <span className="pe-title">{channel?.name} · {pattern.name}</span>
            {[16, 32, 64].map((len) => (
              <button
                key={len}
                className={`pill-btn ${pattern.length === len ? 'active' : ''}`}
                onClick={() => setPatternLength(pattern.id, len)}
              >
                {len}
              </button>
            ))}
            <button className="pill-btn" onClick={newPattern} title="New empty pattern for this channel">
              New
            </button>
          </span>
        )}
        {view === 'playlist' && (
          <span className="pe-controls">
            <span className="pe-title">
              {project.playlist.length > 0
                ? 'Arrangement — playback follows the playlist'
                : 'Empty playlist — playback loops each channel’s pattern'}
            </span>
          </span>
        )}
        {view === 'session' && (
          <span className="pe-controls">
            <span className="pe-title">
              Launch grid — rows are scenes, columns are channels
            </span>
          </span>
        )}
      </div>

      {view === 'pattern' ? (
        channel && pattern ? (
          channel.kind === 'drum' ? (
            <StepGrid
              pattern={pattern}
              rows={rows}
              color={channel.color}
              onToggle={(step, note) => toggleStep(pattern.id, step, note)}
            />
          ) : channel.kind === 'sampler' ? (
            <div className="sampler-stack">
              <SampleEditor channelId={channel.id} />
              <PianoRoll pattern={pattern} color={channel.color} />
            </div>
          ) : (
            <PianoRoll pattern={pattern} color={channel.color} />
          )
        ) : (
          <div className="placeholder">Select a channel in the rack to edit its pattern.</div>
        )
      ) : view === 'playlist' ? (
        <Playlist />
      ) : (
        <SessionView />
      )}
    </main>
  );
}